import {
  StyledPersonalInfo,
  TitleBox,
  MetricContainer,
  Description,
} from "./styled"
import { Title20 } from "components/Typo"
import { CTABox } from "../styled"
import Button from "components/Buttons/Button"
import { useNavigate } from "react-router-dom"
import PATHS from "router/paths"
import { useEffect, useState } from "react"
import { FlexBox } from "components/Common/FlexBox"
import { useSetPageTitle } from "libs/ga"
import HeightInput from "components/inputs/HeightInput"
import DarkSwitch from "components/inputs/DarkSwitch"
import {
  useBody,
  useBodyDisplayUnits,
  useLatestUserScan,
  useScanMessage,
  useUpdateBodyAndUnitsWhenOnboarding,
  useValidateBodyHeight,
  useValidateBodyWeight,
} from "features/profile/hooks/body"
import {
  IBodyDisplayUnits,
  IUpdateBodyAndUnitPayload,
} from "features/profile/profileTypes"
import {
  cmToMm,
  DEFAULT_CM_BODY_HEIGHT,
  feetInchesToMm,
  MAX_CM_BODY_HEIGHT,
  MIN_CM_BODY_HEIGHT,
  mmToCm,
  mmToFeetInches,
} from "utils/heightConverter"
import {
  getBodyWeightValueFromGram,
  getDefaultBodyWeight,
  getMaxBodyWeight,
  getMinBodyWeight,
  kgToGram,
  poundToGram,
} from "utils/weightConverter"
import { bodyDisplayUnitsOptions } from "config"
import BodyErrors from "components/onboarding/BodyErrors"
import OneDecimalWheelPicker from "components/inputs/OneDecimalWheelPicker"
import ScanMessageBanner from "components/ScanMessageBanner"

const PersonalInfo = () => {
  useSetPageTitle("Body Metric - Onboarding")

  const navigate = useNavigate()
  const goToSetGoalPage = () => {
    navigate(PATHS.app.setGoal)
  }

  const { body, getBodyLoading, getBodyFailed } = useBody()
  const { handleUpdateBodyAndUnits, updateBodyAndUnitsLoading } =
    useUpdateBodyAndUnitsWhenOnboarding(goToSetGoalPage)
  const { bodyDisplayUnits: initBodyDisplayUnit } = useBodyDisplayUnits()
  useLatestUserScan() // Fetch latest scan on mount
  const [bodyDisplayUnits, setBodyDisplayUnits] = useState(initBodyDisplayUnit)

  const [gramWeight, setGramWeight] = useState(0)
  const weightError = useValidateBodyWeight(bodyDisplayUnits, gramWeight)

  const [mmHeight, setMmHeight] = useState(0)
  const heightError = useValidateBodyHeight(bodyDisplayUnits, mmHeight)

  const [shouldDisplayError, setShouldDisplayError] = useState(false)

  const [feet, setFeet] = useState(0)
  const [inches, setInches] = useState(0)

  const onFeetInchesChange = (feet: number, inches: number) => {
    setFeet(feet)
    setInches(inches)

    const nextMmHeight = feetInchesToMm(feet, inches)
    setMmHeight(nextMmHeight)
  }

  useEffect(() => {
    if (initBodyDisplayUnit) {
      setBodyDisplayUnits(initBodyDisplayUnit)
    }
  }, [initBodyDisplayUnit])

  useEffect(() => {
    if (body) {
      setGramWeight(body.weight)
      setMmHeight(body.height)

      const { feet, inches } = mmToFeetInches(body.height)

      setFeet(feet)
      setInches(inches)
    }
  }, [body])

  const onWeightChange = (nextWeight: number) => {
    const nextGramWeight =
      bodyDisplayUnits === "IMPERIAL"
        ? poundToGram(nextWeight)
        : kgToGram(nextWeight)
    setGramWeight(nextGramWeight)
  }

  const onCmHeightChange = (nextCmHeight: number) => {
    const nextMmHeight = cmToMm(nextCmHeight)
    setMmHeight(nextMmHeight)
    const { feet, inches } = mmToFeetInches(nextMmHeight)
    setFeet(feet)
    setInches(inches)
  }

  const isWeightChanged = gramWeight !== body?.weight
  const isHeightChanged = mmHeight !== body?.height
  const isBodyDisplayUnitsChanged = bodyDisplayUnits !== initBodyDisplayUnit

  const isProfileChanged =
    isWeightChanged || isHeightChanged || isBodyDisplayUnitsChanged

  const onSubmitUpdateProfile = () => {
    const payload: IUpdateBodyAndUnitPayload = {}
    const hasNotBodyYet = body.height === 0 || body.weight === 0

    if (hasNotBodyYet || isHeightChanged || isWeightChanged) {
      payload.body = {
        height: mmHeight,
        weight: gramWeight,
      }
    }

    if (hasNotBodyYet || isBodyDisplayUnitsChanged) {
      payload.bodyDisplayUnits = bodyDisplayUnits
    }

    if (payload.body || payload.bodyDisplayUnits) {
      handleUpdateBodyAndUnits(payload)
    }
  }

  const goNextStep = () => {
    if (heightError || weightError) {
      setShouldDisplayError(true)
      return
    }

    if (isProfileChanged) {
      onSubmitUpdateProfile()
    } else {
      goToSetGoalPage()
    }
  }

  const scanMessage = useScanMessage({ gramWeight })

  const isDisableSubmit = getBodyFailed
    ? true
    : shouldDisplayError && (!!weightError || !!heightError)

  return (
    <StyledPersonalInfo>
      <TitleBox>
        <Title20>Body metric</Title20>
        <Description>Data fetched from your ZOZOFIT Profile</Description>
      </TitleBox>

      <MetricContainer>
        <DarkSwitch
          onChange={(value) => setBodyDisplayUnits(value as IBodyDisplayUnits)}
          options={bodyDisplayUnitsOptions}
          value={bodyDisplayUnits}
        />
      </MetricContainer>

      <FlexBox $direction="column" $gap={12}>
        {bodyDisplayUnits === "METRIC" ? (
          <OneDecimalWheelPicker
            placeholder="Enter height"
            label="Height"
            value={mmToCm(mmHeight)}
            onChange={onCmHeightChange}
            detailTrailing="cm"
            disabled={getBodyLoading}
            isError={shouldDisplayError && !!heightError}
            maxInteger={MAX_CM_BODY_HEIGHT}
            minInteger={MIN_CM_BODY_HEIGHT}
            defaultInteger={DEFAULT_CM_BODY_HEIGHT}
          />
        ) : (
          <HeightInput
            feet={feet}
            inches={inches}
            onChange={onFeetInchesChange}
            isError={shouldDisplayError && !!heightError}
            disabled={getBodyLoading}
          />
        )}

        <OneDecimalWheelPicker
          placeholder="Enter weight"
          label="Weight"
          value={getBodyWeightValueFromGram(bodyDisplayUnits, gramWeight)}
          onChange={onWeightChange}
          detailTrailing={bodyDisplayUnits === "IMPERIAL" ? "lb" : "kg"}
          disabled={getBodyLoading}
          isError={shouldDisplayError && !!weightError}
          maxInteger={getMaxBodyWeight(bodyDisplayUnits)}
          minInteger={getMinBodyWeight(bodyDisplayUnits)}
          defaultInteger={getDefaultBodyWeight(bodyDisplayUnits)}
        />
      </FlexBox>

      {scanMessage && <ScanMessageBanner message={scanMessage} />}

      {shouldDisplayError && (
        <BodyErrors weightError={weightError} heightError={heightError} />
      )}

      <CTABox>
        <Button
          $borderLess
          $theme="dark"
          loading={updateBodyAndUnitsLoading}
          onClick={goNextStep}
          $type="primary"
          style={{ width: "100%" }}
          disabled={isDisableSubmit}
        >
          {isProfileChanged ? "Update" : "Continue"}
        </Button>
      </CTABox>
    </StyledPersonalInfo>
  )
}

export default PersonalInfo
